import * as React from 'react';
// `React` must stay IN SCOPE even though nothing here names it: knowit-main compiles this package
// with the CLASSIC JSX transform (`"jsx": "react"`, React 17), where every JSX element expands to
// `React.createElement`. slate and coil use `"jsx": "react-jsx"`, where the same import is unused and
// `noUnusedLocals` rejects it — which is why slate consumed this package through a hand-written
// vendor-types shim rather than compiling it. `void React` satisfies both: the binding is used, and
// it is still in scope for the classic transform. Deleting either line breaks knowit-main's build.
void React;

import type { WidgetSeries } from '../types';

/**
 * The four chart renderers, ported from knowit-main's BriefChart (brief-as-publication P1) into
 * the shared package so BOTH editor lanes draw from one implementation (doc-widgets §D2).
 *
 * No styled-components — this file must run wherever React ≥17 runs. Styling is class hooks
 * (`docit-widget-*`) plus minimal inline styles, so hosts theme via CSS without forking pixels.
 *
 * The rule that governs every branch: **a null value is a HOLE, not a zero.** An unmeasured point
 * renders as a gap labeled "not recorded"; drawing it at the baseline would manufacture a
 * measurement nobody made.
 */

const POSITIVE = '#2f9e6e';
const NEGATIVE = '#c2544d';
const NEUTRAL = '#6b7280';
/** Unmeasured. Flat grey on purpose — absence must not look like a small measured value. */
const ABSENT = '#c9ccd1';

export function fmt(v: number | null, unit?: string | null): string {
  if (v === null || Number.isNaN(v)) {
    return 'not recorded';
  }
  const rounded = Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2);
  return unit ? `${rounded}${unit}` : rounded;
}

function pointTitle(label: string, value: number | null, unit?: string | null, state?: string | null, meta?: Record<string, number | string | null>): string {
  const bits = [`${label}: ${fmt(value, unit)}`];
  if (state) {
    bits.push(`(${state})`);
  }
  if (meta) {
    bits.push(Object.entries(meta).map(([k, v]) => `${k}=${v ?? '—'}`).join(', '));
  }
  return bits.join(' · ');
}

/** Signed values around a zero baseline; hollow + dashed = dormant (measured, not firing). */
export function Bars({ series }: { series: WidgetSeries[] }) {
  const points = series[0]?.points ?? [];
  const unit = series[0]?.unit ?? null;
  const measured = points.filter((p) => p.value !== null).map((p) => Math.abs(p.value as number));
  const max = measured.length ? Math.max(...measured) : 1;
  const rowH = 26;

  return (
    <svg className="docit-widget-bars" width="100%" height={points.length * rowH + 8} role="img">
      {points.map((p, i) => {
        const y = i * rowH + 4;
        const isNull = p.value === null;
        const v = (p.value ?? 0) as number;
        const w = isNull ? 0 : Math.max(2, (Math.abs(v) / max) * 46);
        const dormant = (p.state ?? '').toLowerCase() === 'dormant';
        const color = isNull ? ABSENT : v >= 0 ? POSITIVE : NEGATIVE;
        return (
          <g key={p.label + i}>
            <text x={0} y={y + 13} fontSize={11} fill="currentColor" opacity={0.85}>{p.label}</text>
            <line x1="52%" y1={y} x2="52%" y2={y + 18} stroke={ABSENT} strokeWidth={1} />
            {isNull ? (
              <text x="53%" y={y + 13} fontSize={10} fill={ABSENT}>not recorded</text>
            ) : (
              <rect
                x={v >= 0 ? '52%' : `${52 - w}%`}
                y={y + 3}
                width={`${w}%`}
                height={12}
                fill={color}
                fillOpacity={dormant ? 0.28 : 0.9}
                stroke={dormant ? color : 'none'}
                strokeDasharray={dormant ? '3 2' : undefined}
              >
                <title>{pointTitle(p.label, p.value, unit, p.state, p.meta)}</title>
              </rect>
            )}
            <text x="100%" y={y + 13} fontSize={10} textAnchor="end" fill="currentColor" opacity={0.7}>
              {fmt(p.value, unit)}{p.state ? ` · ${p.state}` : ''}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** 0..1 confidences and percentiles. */
export function Gauge({ series }: { series: WidgetSeries[] }) {
  const points = series[0]?.points ?? [];
  const rowH = 24;
  return (
    <svg className="docit-widget-gauge" width="100%" height={points.length * rowH + 8} role="img">
      {points.map((p, i) => {
        const y = i * rowH + 4;
        const isNull = p.value === null;
        const pct = isNull ? 0 : Math.max(0, Math.min(1, p.value as number));
        return (
          <g key={p.label + i}>
            <text x={0} y={y + 12} fontSize={11} fill="currentColor" opacity={0.85}>{p.label}</text>
            <rect x="45%" y={y + 3} width="40%" height={10} fill={ABSENT} fillOpacity={0.35} rx={5} />
            {!isNull && (
              <rect x="45%" y={y + 3} width={`${pct * 40}%`} height={10} fill={NEUTRAL} rx={5}>
                <title>{pointTitle(p.label, p.value)}</title>
              </rect>
            )}
            <text x="100%" y={y + 12} fontSize={10} textAnchor="end" fill="currentColor" opacity={0.7}>
              {isNull ? 'not recorded' : `${Math.round(pct * 100)}%`}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Ordered series. Nulls BREAK the line — a gap is visibly a gap, never bridged. */
export function Sparkline({ series }: { series: WidgetSeries[] }) {
  const points = series[0]?.points ?? [];
  const unit = series[0]?.unit ?? null;
  const vals = points.filter((p) => p.value !== null).map((p) => p.value as number);
  if (!vals.length) {
    return <div className="docit-widget-empty">No recorded values.</div>;
  }
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const w = 100 / Math.max(1, points.length - 1);
  const H = 48;

  const segments: string[][] = [];
  let current: string[] = [];
  points.forEach((p, i) => {
    if (p.value === null) {
      if (current.length) {
        segments.push(current);
      }
      current = [];
      return;
    }
    const x = i * w;
    const y = H - ((p.value - min) / span) * (H - 8) - 4;
    current.push(`${x},${y}`);
  });
  if (current.length) {
    segments.push(current);
  }

  const last = points[points.length - 1];
  const first = vals[0];
  const rising = (last?.value ?? first) >= first;

  return (
    <svg
      className="docit-widget-sparkline"
      width="100%"
      height={H + 16}
      viewBox={`0 0 100 ${H + 16}`}
      preserveAspectRatio="none"
      role="img"
    >
      {segments.map((seg, i) => (
        <polyline
          key={i}
          points={seg.join(' ')}
          fill="none"
          stroke={rising ? POSITIVE : NEGATIVE}
          strokeWidth={1.2}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <text x={0} y={H + 12} fontSize={5} fill="currentColor" opacity={0.7}>{fmt(min, unit)}</text>
      <text x={100} y={H + 12} fontSize={5} textAnchor="end" fill="currentColor" opacity={0.7}>
        {fmt(last?.value ?? null, unit)}
      </text>
    </svg>
  );
}

/** Labeled edges, thickness from a recorded weight. Dashed thin grey = weight not recorded. */
export function Provenance({ series }: { series: WidgetSeries[] }) {
  const points = series[0]?.points ?? [];
  const measured = points.filter((p) => p.value !== null).map((p) => p.value as number);
  const max = measured.length ? Math.max(...measured) : 1;
  const rowH = 22;
  return (
    <svg className="docit-widget-provenance" width="100%" height={points.length * rowH + 8} role="img">
      {points.map((p, i) => {
        const y = i * rowH + 4;
        const isNull = p.value === null;
        const thickness = isNull ? 1 : Math.max(1, ((p.value as number) / max) * 6);
        return (
          <g key={p.label + i}>
            <line
              x1="0"
              y1={y + 9}
              x2="34%"
              y2={y + 9}
              stroke={isNull ? ABSENT : NEUTRAL}
              strokeWidth={thickness}
              strokeDasharray={isNull ? '2 3' : undefined}
            >
              <title>{p.label}: weight {isNull ? 'not recorded' : fmt(p.value)}</title>
            </line>
            <text x="36%" y={y + 13} fontSize={11} fill="currentColor" opacity={0.85}>{p.label}</text>
            <text x="100%" y={y + 13} fontSize={10} textAnchor="end" fill="currentColor" opacity={0.6}>
              {isNull ? 'not recorded' : fmt(p.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
